/**
 * Site-wide footer — content pass §5.
 *
 * Rendered by SiteLayout, so it appears on every marketing route, not just
 * the homepage. The closing CTA band above it on the homepage is a separate
 * component (marketing/FooterCTA).
 *
 * The legal row is not decoration: Privacy, Terms and SMS Terms are linked
 * from here because the A2P 10DLC campaign and the SMS consent flow both
 * require a reachable, persistent link to them. Do not drop them from a
 * redesign — see docs/A2P_10DLC_SUBMISSION.md.
 *
 * Hours render from config through the shared formatter rather than as
 * stored strings, so the footer cannot disagree with the visit section.
 */

import { Link } from 'react-router-dom';
import { useCopy, useLocale, localePrefix } from '../../content';
import { useShopConfig } from '../../hooks/useShopConfig';
import { timeRange } from '../../lib/formatTime';
import { Icon, type IconName } from '../icons';

export default function Footer() {
  const copy = useCopy();
  const c = copy.footer;
  const { t, shop, hours, legal } = useShopConfig();
  const locale = useLocale();
  const p = localePrefix(locale);

  const row = (day: string) => hours.find((h) => h.day === day);
  const fmt = (d?: { open: string | null; close: string | null }) =>
    d?.open && d.close ? timeRange(d.open, d.close, locale) : copy.meta.closed;

  const hoursLines = [
    `${copy.meta.weekdayRange} · ${fmt(row('Mon'))}`,
    `${copy.meta.days.sat} · ${fmt(row('Sat'))}`,
    `${copy.meta.days.sun} · ${fmt(row('Sun'))}`,
  ];

  // Services is a real route; the rest are homepage sections.
  const explore = [
    { label: copy.nav.home, href: `${p}/` },
    { label: copy.nav.about, href: `${p}/about` },
    { label: copy.nav.services, href: `${p}/services` },
    { label: copy.nav.barbers, href: `${p}/#barbers` },
    { label: copy.nav.gallery, href: `${p}/#gallery` },
    { label: copy.nav.visit, href: `${p}/#visit` },
  ];

  // Booksy row links bookingUrl, not booksy_shop_url: the site has exactly
  // one booking destination, and its label is a Book CTA like any other.
  const social = [
    { icon: 'instagram' as IconName, label: `@${shop.social.instagram.handle}`, url: shop.social.instagram.url },
    { icon: 'facebook' as IconName, label: shop.social.facebook.handle, url: shop.social.facebook.url },
    { icon: 'tiktok' as IconName, label: `@${shop.social.tiktok.handle}`, url: shop.social.tiktok.url },
    { icon: 'external' as IconName, label: t(c.booksyCta), url: shop.bookingUrl },
  ].filter((s) => Boolean(s.url));

  const link = 'text-edge-2 transition-colors hover:text-white';

  return (
    <footer className="bg-navy px-6 py-14 text-white md:px-12 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-12 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <img
                src="/assets/logo/kd-crest-square.png"
                alt=""
                role="presentation"
                width={56}
                height={56}
                className="h-14 w-14 object-contain"
              />
              <span className="flex flex-col">
                <span className="font-newsreader text-[26px] font-semibold leading-tight">
                  {shop.name}
                </span>
                {/* Abbreviated, unlike the header's. The header is a wide bar
                    where "FLORIDA" fits on one line; this column is a quarter
                    of the grid and wraps it. */}
                <span className="mt-1 whitespace-nowrap font-oswald text-[10px] uppercase tracking-[0.18em] text-warm-muted">
                  {t(copy.tagline.footer)}
                </span>
              </span>
            </div>

            <div className="mt-6 flex flex-col gap-1 font-newsreader text-[17px] text-edge-2">
              <span>{shop.address_line_1}</span>
              <span>
                {shop.city}, {shop.state} {shop.zip}
              </span>
            </div>
            <a href={`tel:${shop.phone_e164}`} className={`mt-3 inline-block font-newsreader text-[17px] ${link}`}>
              {shop.phone_display}
            </a>
          </div>

          <div>
            <h3 className="font-oswald text-[12px] uppercase tracking-[0.24em] text-gold">
              {copy.meta.hoursLabel}
            </h3>
            <ul className="mt-4 flex flex-col gap-2 font-newsreader text-[17px] text-edge-2">
              {hoursLines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-oswald text-[12px] uppercase tracking-[0.24em] text-gold">
              {t(c.followLabel)}
            </h3>
            <ul className="mt-4 flex flex-col gap-3">
              {social.map((s) => (
                <li key={s.url}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer me"
                    className={`flex items-center gap-2 font-newsreader text-[17px] ${link}`}
                  >
                    <Icon name={s.icon} />
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-oswald text-[12px] uppercase tracking-[0.24em] text-gold">
              {t(c.exploreLabel)}
            </h3>
            <ul className="mt-4 flex flex-col gap-2">
              {explore.map((e) => (
                <li key={e.href}>
                  <a href={e.href} className={`font-newsreader text-[17px] ${link}`}>
                    {t(e.label)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-navy-line pt-8 font-newsreader text-[14px] text-warm-muted md:mt-16 md:flex-row md:items-center md:justify-between">
          <span>{t(c.signoff)}</span>

          {/* Required by the A2P campaign and the SMS consent flow. */}
          <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <Link to={`${p}${legal.privacy_url}`} className={link}>
              {t(c.legal.privacy)}
            </Link>
            <span aria-hidden>·</span>
            <Link to={`${p}${legal.terms_url}`} className={link}>
              {t(c.legal.terms)}
            </Link>
            <span aria-hidden>·</span>
            <Link to={`${p}${legal.sms_terms_url}`} className={link}>
              {t(c.legal.smsTerms)}
            </Link>
          </span>

          <span>{t(c.legal.builtBy)}</span>
        </div>
      </div>
    </footer>
  );
}
